import { ApplicationRef, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Modal, ModalActionType } from 'components/modal/modal.model';
import { Subject, concat, interval, filter, first, switchMap, takeUntil } from 'rxjs';

import { LocalDeviceUtility } from 'weavix-shared/utils/local-device-utility';
import { SnackBarComponent } from 'components/snack-bar/snack-bar.component';
import { environment } from 'environments/environment';
import { AlertService } from 'weavix-shared/services/alert.service';
import { PttService } from 'weavix-shared/services/ptt.service';
import { AnalyticsService } from './core/services/analytics.service';
import { AppService } from './core/services/app.service';
import { TranslationService } from './core/services/translation/translation.service';
import { TitleCasePipe } from '@angular/common';
import { OverlayContainer } from '@angular/cdk/overlay';
import { BROWSERS, DeviceDetectorService } from 'ngx-device-detector';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [TitleCasePipe],
})
export class AppComponent implements OnInit, OnDestroy {
    appLoading: boolean;
    private readonly destroyed$ = new Subject<boolean>();

    permissionModal?: Modal;

    unauthenticated: () => void;
    initialized: boolean;
    teamsApp = environment.teamsApp;

    constructor(
        private readonly elementRef: ElementRef,
        private readonly overlayContainer: OverlayContainer,
        private readonly translationService: TranslationService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly matSnackBar: MatSnackBar,
        private readonly alertsService: AlertService,
        private readonly swUpdate: SwUpdate,
        private readonly appRef: ApplicationRef,
        private readonly appService: AppService,
        private readonly pttService: PttService,
        private readonly titleCasePipe: TitleCasePipe,
        private readonly deviceService: DeviceDetectorService,
        _analyticsService: AnalyticsService,
    ) {
        this.pttService.prepare();
        this.appService.setAppDetails(fn => {
            this.unauthenticated = fn;
            this.checkLogin();
        });
        this.translationService.setInitialLanguage();

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                let r = this.route.firstChild;
                while (r) {
                    if (r.firstChild) {
                        r = r.firstChild;
                    } else {
                        break;
                    }
                }
                handleRouteTelemetry(event.url);
            }
        });

        const handleRouteTelemetry = (url: string) => {
            const parts = url.split('?')[0].split('/').slice(1);
            if (parts[0] !== 'login') parts.splice(0, 2);
            const prettyName = this.titleCasePipe.transform(`Web-Radio ${parts.join(' ')}`);
            AnalyticsService.page(prettyName, url);
        };

        const promptDaUpdate = () => {
            this.alertsService.sendPrompt({
                messageKey: 'pwa.update-title',
                buttonProps: { label: 'pwa.update-refresh', icon: { faIcon: 'fas fa-sync' } },
            }).pipe(
                filter(action => action !== false),
                switchMap(() => this.swUpdate.activateUpdate()),
                takeUntil(this.destroyed$),
            ).subscribe(() => {
                console.log('reloading due to pwa update');
                document.location.reload();
            });
        };

        if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates
                .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
                .subscribe(() => {
                    promptDaUpdate();
                });

            const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
            concat(appIsStable$, interval(3 * 60 * 60 * 1000)).subscribe(() => this.swUpdate.checkForUpdate());
        }

        LocalDeviceUtility.denied$.subscribe(() => {
            this.permissionModal = {
                isOpen: true,
                width: 600,
                actions: {
                    submit: { show: true, textKey: 'generics.ok' },
                },
                fullScreen: false,
                content: true,
                header: {
                    textKey: environment.teamsApp ? 'meeting-join.denied-permissions-header' : null,
                    textAlignment: 'left',
                },
            };
        });
    }

    permissionClose(_type: ModalActionType) {
        this.permissionModal = null;
    }

    get browserError() {
        if (this.teamsApp) return 'meeting-join.denied-teams';
        const browser = this.deviceService.getDeviceInfo().browser;
        if (browser === BROWSERS.MS_EDGE_CHROMIUM) return 'meeting-join.denied-edge-chromium';
        if (browser === BROWSERS.MS_EDGE) return 'meeting-join.denied-edge';
        if (browser === BROWSERS.CHROME) return 'meeting-join.denied-chrome';
        if (browser === BROWSERS.FIREFOX) return 'meeting-join.denied-firefox';
        return null;
    }

    async ngOnInit() {
        this.appLoading = false;
        this.openAlertSubscription();
        this.openAppLoadingSubscription();
        this.initialized = true;
        this.checkLogin();
    }

    checkLogin() {
        if (this.initialized && this.unauthenticated) {
            this.unauthenticated();
        }
    }

    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    private openAlertSubscription(): void {
        this.alertsService.alert$
            .pipe(takeUntil(this.destroyed$))
            .subscribe(res => {
                if (this.unauthenticated) return;

                // calls `createCustomOverlayContainer` on `overlayContainer` to append the alert overlay to the found container
                // falls back to searching for `channel-container` class if parentContainer is not provided
                const alertContainer = res.parentContainer?.nativeElement ?? this.elementRef.nativeElement.querySelector('.channel-container');
                if (alertContainer) {
                    this.overlayContainer['createCustomOverlayContainer'](alertContainer);
                }

                const sb = this.matSnackBar.openFromComponent(SnackBarComponent, {
                    duration: res.timeout,
                    verticalPosition: 'bottom',
                    panelClass: ['snap-to-bottom', res.cssClass],
                    data: res,
                });

                // removing any custom overlay containers that were previously appended, ensuring that the overlay environment is reset.
                if (alertContainer) {
                    this.overlayContainer['clearCustomOverlayContainer']();
                }

                if (!res.response) return;
                sb.afterDismissed()
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe(() => {
                        res.response.next(false);
                    });
            });
    }

    private openAppLoadingSubscription(): void {
        AlertService.appLoadingSubject
            .pipe(takeUntil(this.destroyed$))
            .subscribe(res => {
                this.appLoading = res;
            });
    }
}
